import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const ATTRIBUTION_STORAGE_KEY = 'ph_charts_inc_attribution';
const HANDOFF_ATTRIBUTION_MS = 30 * 24 * 60 * 60 * 1000;
const HANDOFF_AT_FORMAT = 'DD-MM-YYYY HH:mm:ss';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const HANDOFF_PARAM_KEYS = ['handoff_id'];

export function isValidHandoffId(value) {
  if (typeof value !== 'string') {
    return false;
  }
  const id = value.trim();
  return id.length > 0 && id.length <= 64;
}

function toHandoffAtMs(value) {
  if (value == null || value === '') {
    return 0;
  }
  const parsed = typeof value === 'number'
    ? dayjs(value)
    : dayjs(value, HANDOFF_AT_FORMAT, true).isValid()
      ? dayjs(value, HANDOFF_AT_FORMAT)
      : dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

function formatHandoffAt(value) {
  const ms = toHandoffAtMs(value);
  return ms ? dayjs(ms).format(HANDOFF_AT_FORMAT) : null;
}

function readStore() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStore(store) {
  if (typeof window === 'undefined' || !store) {
    return;
  }
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore storage failures.
  }
}

function readUtmsFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const utms = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utms[key] = value;
    }
  });
  return utms;
}

function stripHandoffFromUrl() {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  let changed = false;
  HANDOFF_PARAM_KEYS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
  }
}

function mergeHandoffIntoStore(handoffId, { landingPath, utms, receivedAt, source } = {}) {
  const now = receivedAt || Date.now();
  const existing = readStore() || {};
  const nextUtms = utms && Object.keys(utms).length > 0 ? utms : undefined;

  const store = {
    ...existing,
    last_handoff_id: handoffId,
    last_handoff_at: now,
    ...(source ? { source } : {}),
    ...(landingPath ? { landing_path: landingPath } : {}),
    ...(nextUtms ? { last_touch_utms: nextUtms } : {}),
  };

  if (!existing.first_handoff_id) {
    store.first_handoff_id = handoffId;
    if (nextUtms) {
      store.first_touch_utms = nextUtms;
    }
  }

  writeStore(store);
  return store;
}

function isHandoffEligible(store) {
  if (!store?.last_handoff_id || !store?.last_handoff_at) {
    return false;
  }
  return Date.now() - toHandoffAtMs(store.last_handoff_at) <= HANDOFF_ATTRIBUTION_MS;
}

function flatUtmsFromStore(store) {
  if (!store) {
    return {};
  }
  return {
    ...(store.first_touch_utms || {}),
    ...(store.last_touch_utms || {}),
  };
}

export function getChartsAttributionContext() {
  const store = readStore();
  const storedUtms = flatUtmsFromStore(store);

  if (!store || !isHandoffEligible(store)) {
    return {
      landing_path: store?.landing_path,
      entry_source: storedUtms.utm_source ? 'utm' : 'direct',
      ...storedUtms,
    };
  }

  return {
    landing_path: store.landing_path,
    entry_source: 'inc',
    handoff_id: store.last_handoff_id,
    first_handoff_id: store.first_handoff_id,
    last_handoff_id: store.last_handoff_id,
    ...storedUtms,
  };
}

export function getAttributionApiPayload() {
  const store = readStore();
  if (!store || !isHandoffEligible(store)) {
    return null;
  }
  const lastHandoffAt = formatHandoffAt(store.last_handoff_at);
  if (!lastHandoffAt) {
    return null;
  }
  return {
    handoff_id: store.last_handoff_id,
    first_handoff_id: store.first_handoff_id,
    last_handoff_id: store.last_handoff_id,
    landing_path: store.landing_path,
    source: store.source,
    first_touch_utms: store.first_touch_utms || {},
    last_touch_utms: store.last_touch_utms || {},
    last_handoff_at: lastHandoffAt,
  };
}

export function applyServerHandoff(serverBlob) {
  if (!serverBlob?.last_handoff_id || !serverBlob?.last_handoff_at) {
    return;
  }
  const serverAt = toHandoffAtMs(serverBlob.last_handoff_at);
  if (!serverAt) {
    return;
  }
  const existing = readStore() || {};
  const next = { ...existing };
  if (!existing.first_handoff_id) {
    next.first_handoff_id = serverBlob.first_handoff_id;
    next.first_touch_utms = serverBlob.first_touch_utms;
  }
  if (!existing.last_handoff_at || serverAt > toHandoffAtMs(existing.last_handoff_at)) {
    next.last_handoff_id = serverBlob.last_handoff_id;
    next.last_handoff_at = serverAt;
    next.last_touch_utms = serverBlob.last_touch_utms;
    next.source = serverBlob.source;
    next.landing_path = existing.landing_path || serverBlob.landing_path;
  }
  writeStore(next);
}

export function getHandoffPayloadToSync(serverBlob) {
  const store = readStore();
  if (!store || !isHandoffEligible(store)) {
    return null;
  }
  if (toHandoffAtMs(store.last_handoff_at) <= toHandoffAtMs(serverBlob?.last_handoff_at)) {
    return null;
  }
  return getAttributionApiPayload();
}

export function initChartsAttribution() {
  if (typeof window === 'undefined') {
    return { handoffReceived: false };
  }

  const params = new URLSearchParams(window.location.search);
  const rawHandoff = params.get('handoff_id');
  const handoffId = rawHandoff?.trim();
  const utms = readUtmsFromSearch(window.location.search);

  let handoffReceived = false;
  if (handoffId && isValidHandoffId(handoffId)) {
    mergeHandoffIntoStore(handoffId, {
      landingPath: window.location.pathname || '/',
      utms,
      source: params.get('ph_from')?.trim(),
      receivedAt: Date.now(),
    });
    handoffReceived = true;
    stripHandoffFromUrl();
  } else if (!handoffId) {
    const store = readStore();
    if (!store?.landing_path) {
      writeStore({
        ...(store || {}),
        landing_path: window.location.pathname || '/',
        ...(Object.keys(utms).length > 0 ? { last_touch_utms: utms } : {}),
      });
    }
  }

  return { handoffReceived, handoffId: handoffReceived ? handoffId : undefined };
}

export { UTM_KEYS };
